import { useEffect, useMemo, useState } from 'react';
import { Streamdown, defaultRemarkPlugins } from 'streamdown';
import remarkBreaks from 'remark-breaks';
import {
  resolveVariables,
  type VariableContext,
} from '@/libs/correspondence';
import type {
  MessageComposeEvidence,
  MessageComposeEvidenceKind,
  MessageComposeInput,
  MessageComposeKind,
  MessageComposeRecipient,
  MessageComposeSender,
  MessageComposeVariant,
} from '@/api/ai/ai.types';

/**
 * Plugin list for the drafted body. Adds `remark-breaks` (every `\n` → `<br>`)
 * on top of Streamdown's defaults so signatures render on their own line —
 * CommonMark collapses single newlines inside a paragraph to a space, which
 * turns `Mit freundlichen Grüßen\nChristian Hauenschild` into one visual
 * line. `remarkPlugins` *replaces* defaults on this component, so we keep
 * GFM by explicitly concatenating with `defaultRemarkPlugins`.
 */
const BODY_REMARK_PLUGINS = [
  ...Object.values(defaultRemarkPlugins),
  remarkBreaks,
];

/**
 * Renders the `message_compose_v1` tool call as an interactive composer card:
 * a variant tab strip, the selected body (and subject when the kind is
 * `email`), and a kind-aware primary action — "In Mail öffnen" for emails,
 * "In Nachrichten öffnen" for text messages, "Kopieren" for everything else.
 * The tool itself is purely a rendering directive — no result is sent back.
 */
interface MessageComposeCardProps {
  input: MessageComposeInput;
}

/** Label + ARIA name for the primary action, by message kind. */
const PRIMARY_ACTION: Record<
  MessageComposeKind,
  { label: string; ariaLabel: string }
> = {
  email: { label: 'In Mail öffnen', ariaLabel: 'Entwurf im Mail-Programm öffnen' },
  textMessage: {
    label: 'In Nachrichten öffnen',
    ariaLabel: 'Entwurf in Nachrichten öffnen',
  },
  other: { label: 'Kopieren', ariaLabel: 'Entwurf in die Zwischenablage kopieren' },
};

/** Subject + body composed for the system mail handler. */
function buildMailtoHref(variant: MessageComposeVariant): string {
  const params = new URLSearchParams();
  if (variant.subject) params.set('subject', variant.subject);
  params.set('body', variant.body);
  return `mailto:?${params.toString()}`;
}

/** Body composed for the system SMS handler. */
function buildSmsHref(variant: MessageComposeVariant): string {
  return `sms:?&body=${encodeURIComponent(variant.body)}`;
}

/**
 * Builds the `VariableContext` consumed by `@/libs/correspondence` from the
 * recipient + sender blobs threaded through the tool input. Returns a stable
 * context for `null`/`null` so unresolved `{{anrede}}` / `{{grussformel}}`
 * tokens still render visibly (we use `keep`-mode resolution by default).
 */
function buildVariableContext(
  recipient: MessageComposeRecipient | undefined,
  sender: MessageComposeSender | undefined,
): VariableContext {
  return {
    recipient: recipient
      ? {
          firstName: recipient.firstName || null,
          lastName: recipient.lastName || null,
          email: null,
          salutation: recipient.salutation,
          gender: recipient.gender,
          businessPartnerName: recipient.businessPartnerName || null,
        }
      : null,
    sender: sender
      ? {
          firstName: sender.firstName,
          lastName: sender.lastName,
          email: sender.email,
        }
      : null,
  };
}

/**
 * Resolves a variant's tokens against the variable context. `body` is
 * Markdown-bearing — we resolve in `text` mode so the resulting string
 * still flows through Streamdown for paragraph / list rendering; the
 * resolver does not produce HTML in this path. `subject` is plain text.
 *
 * Memoised by the caller so editing the same variant body in subsequent
 * renders doesn't rerun the regex.
 */
function resolveVariant(
  variant: MessageComposeVariant,
  ctx: VariableContext,
): MessageComposeVariant {
  return {
    ...variant,
    body: resolveVariables(variant.body, ctx, { mode: 'text' }),
    subject:
      variant.subject != null
        ? resolveVariables(variant.subject, ctx, { mode: 'text' })
        : variant.subject,
  };
}

/** Short label per evidence kind, shown as a chip prefix. */
const EVIDENCE_KIND_LABEL: Record<MessageComposeEvidenceKind, string> = {
  'business-partner': 'GP',
  contact: 'Kontakt',
  order: 'Auftrag',
  item: 'Artikel',
  other: 'Quelle',
};

function EvidenceRow({ items }: { items: MessageComposeEvidence[] }) {
  if (items.length === 0) return null;
  return (
    <div className="eos-asst-mc-evidence">
      <span className="eos-asst-mc-evidence-label">Basiert auf</span>
      <div className="eos-asst-mc-evidence-chips">
        {items.map((e, i) => (
          <span
            key={`${e.kind}-${e.id}-${i}`}
            className={`eos-asst-mc-evidence-chip is-${e.kind}`}
            title={e.note}
          >
            <span className="eos-asst-mc-evidence-chip-kind">
              {EVIDENCE_KIND_LABEL[e.kind]}
            </span>
            <span className="eos-asst-mc-evidence-chip-id">#{e.id}</span>
            {e.note && (
              <span className="eos-asst-mc-evidence-chip-note">{e.note}</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Single-shot clipboard write with a short "Kopiert" confirmation flash. */
function useCopyFlash() {
  const [flashed, setFlashed] = useState(false);
  useEffect(() => {
    if (!flashed) return;
    const t = window.setTimeout(() => setFlashed(false), 1400);
    return () => window.clearTimeout(t);
  }, [flashed]);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlashed(true);
    } catch {
      // Clipboard API can be blocked (permissions, insecure context) — fail
      // quietly; the body is visible on screen so the user can copy by hand.
    }
  };
  return { flashed, copy };
}

export function MessageComposeCard({ input }: MessageComposeCardProps) {
  const variants = input.variants;
  const [activeIndex, setActiveIndex] = useState(0);
  const { flashed, copy } = useCopyFlash();

  // Variable resolution: the AI emits `{{anrede}}` / `{{grussformel}}` rather
  // than literal greetings; we substitute them client-side against the
  // recipient + sender blobs the agent threaded through. Memoised on the
  // input fields so re-renders during streaming don't redo the regex on
  // already-resolved variants.
  const variableContext = useMemo(
    () => buildVariableContext(input.recipient, input.sender),
    [input.recipient, input.sender],
  );
  const resolvedVariants = useMemo(
    () => variants.map((v) => resolveVariant(v, variableContext)),
    [variants, variableContext],
  );

  // Defensive — should never happen given the backend min(1), but a stream
  // glitch could in principle deliver an empty array.
  if (resolvedVariants.length === 0) return null;

  const safeIndex = Math.min(activeIndex, resolvedVariants.length - 1);
  const variant = resolvedVariants[safeIndex];
  const isEmail = input.kind === 'email';
  const primary = PRIMARY_ACTION[input.kind];

  const handlePrimary = () => {
    if (input.kind === 'email') {
      window.location.href = buildMailtoHref(variant);
    } else if (input.kind === 'textMessage') {
      window.location.href = buildSmsHref(variant);
    } else {
      void copy(variant.body);
    }
  };

  const handleCopyBody = () => {
    void copy(variant.body);
  };

  return (
    <div className="eos-asst-mc">
      <div className="eos-asst-mc-head">
        <span className="eos-asst-mc-kind">
          {input.kind === 'email'
            ? 'E-Mail-Entwurf'
            : input.kind === 'textMessage'
              ? 'Nachricht'
              : 'Nachrichtenentwurf'}
        </span>
        {input.summary_title && (
          <span className="eos-asst-mc-title">{input.summary_title}</span>
        )}
      </div>

      {resolvedVariants.length > 1 && (
        <div className="eos-asst-mc-tabs" role="tablist">
          {resolvedVariants.map((v, i) => {
            const active = i === safeIndex;
            return (
              <button
                key={`${v.label}-${i}`}
                type="button"
                role="tab"
                aria-selected={active}
                className={`eos-asst-mc-tab${active ? ' is-active' : ''}`}
                onClick={() => setActiveIndex(i)}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      )}

      {resolvedVariants.length === 1 && (
        <span className="eos-asst-mc-onelabel">{variant.label}</span>
      )}

      {isEmail && variant.subject && (
        <div className="eos-asst-mc-subject">
          <span className="eos-asst-mc-subject-label">Betreff</span>
          <span className="eos-asst-mc-subject-value">{variant.subject}</span>
        </div>
      )}

      <div className="eos-asst-mc-body eos-asst-md">
        <Streamdown controls={false} remarkPlugins={BODY_REMARK_PLUGINS}>
          {variant.body}
        </Streamdown>
      </div>

      {input.evidence && input.evidence.length > 0 && (
        <EvidenceRow items={input.evidence} />
      )}

      <div className="eos-asst-mc-actions">
        <button
          type="button"
          className="eos-asst-mc-primary"
          aria-label={primary.ariaLabel}
          onClick={handlePrimary}
        >
          {primary.label}
        </button>
        {input.kind !== 'other' && (
          <button
            type="button"
            className="eos-asst-mc-secondary"
            aria-label="Entwurf in die Zwischenablage kopieren"
            onClick={handleCopyBody}
          >
            {flashed ? 'Kopiert' : 'Kopieren'}
          </button>
        )}
        {input.kind === 'other' && flashed && (
          <span className="eos-asst-mc-flash">Kopiert</span>
        )}
      </div>
    </div>
  );
}
